import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react'
import { en, type TKey } from './en'
import { fa } from './fa'
import { configureDates } from '../lib/date'

export type Lang = 'en' | 'fa'

export const LANGS: { id: Lang; label: string; dir: 'ltr' | 'rtl' }[] = [
  { id: 'en', label: 'English', dir: 'ltr' },
  { id: 'fa', label: 'فارسی', dir: 'rtl' },
]

const DICTS: Record<Lang, Record<TKey, string>> = { en, fa }

/**
 * `fa-IR` on its own would render dates in the Solar Hijri calendar. Every date
 * in this app is an external deadline published by an Australian institution,
 * so we keep the Gregorian calendar and take only the Farsi language and
 * Persian digits. Swap to plain `fa-IR` if Jalali dates are ever wanted.
 */
export const LOCALES: Record<Lang, string> = {
  en: 'en-AU',
  fa: 'fa-IR-u-ca-gregory',
}

export type Translate = (key: TKey, params?: Record<string, string | number>) => string

interface I18n {
  lang: Lang
  dir: 'ltr' | 'rtl'
  locale: string
  t: Translate
}

const Ctx = createContext<I18n | null>(null)

/**
 * Numeric params are formatted with the active locale, not stringified — under
 * `fa` that is the difference between «۷ روز دیگر» and «7 روز دیگر». Doing it
 * here rather than at each call site is what keeps digits consistent across the
 * whole app; callers pass plain numbers and never think about it.
 */
function interpolate(
  template: string,
  locale: string,
  params?: Record<string, string | number>,
): string {
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (whole, key: string) => {
    if (!(key in params)) return whole
    const value = params[key]
    return typeof value === 'number'
      ? new Intl.NumberFormat(locale, { useGrouping: false }).format(value)
      : String(value)
  })
}

export function I18nProvider({ lang, children }: { lang: Lang; children: ReactNode }) {
  const dict = DICTS[lang] ?? en
  const locale = LOCALES[lang] ?? LOCALES.en
  const dir = lang === 'fa' ? 'rtl' : 'ltr'

  const t = useCallback<Translate>(
    (key, params) => interpolate(dict[key] ?? en[key] ?? key, locale, params),
    [dict, locale],
  )

  // Date helpers live outside React (they are called from selectors and plain
  // functions), so hand them the locale and the words they need up front.
  configureDates(locale, {
    today: t('time.today'),
    tomorrow: t('time.tomorrow'),
    yesterday: t('time.yesterday'),
    inDays: (n) => t('time.inDays', { n }),
    daysAgo: (n) => t('time.daysAgo', { n }),
    overdue: t('time.overdue'),
    dueToday: t('time.dueToday'),
    days: (n) => t('time.days', { n }),
    weeks: (n) => t('time.weeks', { n }),
    hours: (n) => t('time.hours', { n }),
    minutes: (n) => t('time.minutes', { n }),
    hoursMinutes: (h, m) => t('time.hoursMinutes', { h, m }),
    morning: t('time.morning'),
    afternoon: t('time.afternoon'),
    evening: t('time.evening'),
  })

  const value = useMemo<I18n>(() => ({ lang, dir, locale, t }), [lang, dir, locale, t])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useI18n(): I18n {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>')
  return ctx
}

/** Shorthand for the common case. */
export function useT(): Translate {
  return useI18n().t
}

/** Best-guess starting language from the browser, used only on first run. */
export function detectLang(): Lang {
  try {
    return navigator.languages?.some((l) => l.toLowerCase().startsWith('fa')) ? 'fa' : 'en'
  } catch {
    return 'en'
  }
}

export type { TKey }
